import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Book, BookBasic, Image, Like } from '@radium/ui';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  collectionChanges,
  CollectionReference,
  doc,
  docData,
  DocumentChange,
  documentId,
  DocumentReference,
  endBefore,
  FieldPath,
  Firestore,
  getDocs,
  increment,
  limit,
  limitToLast,
  orderBy,
  OrderByDirection,
  query,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';

@Injectable({ providedIn: 'root' })
export class FirebaseBookService {
  // Source data
  private LOADING = new BehaviorSubject(false);
  // private query: QueryConfig;

  bookColl: CollectionReference<Book>;
  bookBasicColl: CollectionReference<BookBasic>;
  tagColl: CollectionReference<any>;

  constructor(private afs: Firestore, private http: HttpClient) {
    this.bookColl = collection(this.afs, 'books') as CollectionReference<Book>;
    this.bookBasicColl = collection(
      this.afs,
      'books_basic'
    ) as CollectionReference<BookBasic>;
    this.tagColl = collection(this.afs, 'tags') as CollectionReference<any>;
  }

  // BOOK METHODS

  getBook(bookDocId: string): Observable<Book> {
    const bookDoc = doc(this.bookColl, bookDocId) as DocumentReference<Book>;
    return docData(bookDoc, { idField: 'id' }).pipe(catchError((x) => of(x)));
  }

  getAllBookDocs(
    key: string | FieldPath,
    operator: any,
    value: any,
    order?: OrderByDirection
  ) {
    return collectionChanges<Book>(
      query<Book>(this.bookColl, where(key, operator, value))
    ).pipe(
      map((arr) => {
        const values = arr.map((snap) => {
          const data: Book = snap.doc.data();
          const doc = snap.doc;
          return { ...data, doc };
        });
        return values;
      }),
      catchError((x) => of(x))
    );
  }

  getFilteredBookDocs(
    key: string | FieldPath,
    operator: any,
    value: any,
    approved: boolean
  ) {
    return collectionChanges<Book>(
      query<Book>(
        this.bookColl,
        where(key, operator, value),
        where('approved', '==', approved)
      )
    ).pipe(
      map((arr) => {
        this.LOADING.next(true);
        const values = arr.map((snap) => {
          const data: Book = snap.doc.data();
          const doc = snap.doc;
          return { ...data, doc };
        });
        return values;
      }),
      catchError((x) => of(x))
    );
  }

  createBook(newData: Book) {
    const data: Book = {
      createdAt: Timestamp.now(),
      viewedByAdmin: false,
      likes: [],
      views: 0,
      isDraft: false,
      approved: true,
      ...newData,
    };
    console.log({ data });

    // return this.bookColl.add(data).then(async (bookRef) => await bookRef.get());
    return addDoc(this.bookColl, data).catch();

    // let document;
    // let documentUuid;

    // if (bookId === "0") {
    //   // Generate "locally" a new document in a collection
    //   document = this.bookColl.ref.doc();
    //   documentUuid = document.id;
    // } else {
    //   document = this.bookBasicColl.doc(bookId);
    //   documentUuid = bookId;
    // }

    // return document.set(data, { merge: true }).then((j) => {
    //   const basicData: BookBasic = {
    //     position: address.position,
    //     approved: false,
    //     bookId: documentUuid,
    //     authorId,
    //     authorType,
    //     packageName,
    //   };
    //
    //   this.bookBasicColl
    //     .doc(documentUuid)
    //     .set(basicData, { merge: true })
    //     .catch();
    //   return j;
    // });
  }

  getBookDocsByCategory(categoryID: number): any {
    return collectionChanges<Book>(
      query<Book>(
        this.bookColl,
        where('bookInfo.bookCategory', '==', categoryID)
      )
    ).pipe(
      map((arr) => {
        this.LOADING.next(true);
        const values = arr.map((snap) => {
          const data: Book = snap.doc.data();
          const doc = snap.doc;
          return { ...data, doc };
        });
        return values;
      }),
      catchError((x) => of(x))
    );
  }

  getProductsWithQuantitiesLessThanOne(productIds: string[]) {
    const queryId = documentId();

    const q = query(this.bookColl, where(queryId, 'in', productIds));
    return getDocs(q).then(({ docs }) => {
      const docsData = docs.map((doc) => doc.data());
      return docsData.filter((x) => x?.quantity < 1);
    });
  }

  tempCreate(id: string, basicData: BookBasic) {
    const bookBasicRef = doc(
      this.afs,
      'books_basic',
      id
    ) as DocumentReference<BookBasic>;
    return setDoc(bookBasicRef, basicData, { merge: true }).catch();
  }

  // async updateAllBookInfo(uid: string, data: Book) {
  //   console.log(data);
  //
  //   const book: Partial<Book> = {
  //     bookId: data.bookId,
  //     authorId: data.authorId,
  //     viewed_by_admin: data.viewed_by_admin,
  //     createdAt: data.createdAt,
  //     views: data.views
  //   };
  //
  //   const cleanedObj = this.cleanAnObject(book);
  //
  //   await this.updateBook(uid, cleanedObj);
  // }

  updateBook(docId: string, data: Partial<Book>) {
    const bookRef = doc(this.afs, 'books', docId) as DocumentReference<Book>;
    return updateDoc(bookRef, data as any).catch();
  }

  removeBookImage(bookId: string, image: Image) {
    this.updateBook(bookId, {
      images: arrayRemove(image) as any,
    }).catch();
  }

  updateBookBasic(docId: string, data: Partial<BookBasic>) {
    const bookBasicRef = doc(
      this.afs,
      'books_basic',
      docId
    ) as DocumentReference<BookBasic>;
    return updateDoc(bookBasicRef, data as any).catch();
  }

  updateBookViews(bookId: string) {
    return this.updateBook(bookId, {
      views: increment(1) as any,
    });
  }

  addBookLike(bookId: string, uid: string) {
    this.updateBook(bookId, {
      likes: arrayUnion({
        authorId: uid,
        createdAt: Timestamp.now(),
      }) as any,
    }).catch();
  }

  removeBookLike(bookId: string, like: Like) {
    this.updateBook(bookId, {
      likes: arrayRemove(like) as any,
    }).catch();
  }

  searchBooksOnGoogle(search: string): Observable<any> {
    // eslint-disable-next-line max-len
    const fields = `volumeInfo/description,volumeInfo/title,volumeInfo/authors,volumeInfo/publisher,volumeInfo/publishedDate,volumeInfo/pageCount,volumeInfo/categories,volumeInfo/averageRating,volumeInfo/maturityRating,volumeInfo/imageLinks,volumeInfo/language,volumeInfo/previewLink`;

    return this.http.get(
      `https://www.googleapis.com/books/v1/volumes?q=${search}&fields=items(${fields})`
    );
  }

  getAllBooks(limiT: number): Observable<Book[]> {
    return collectionChanges<Book>(
      query<Book>(
        this.bookColl,
        where('quantity', '>', 0),
        orderBy('quantity'),
        orderBy('title'),
        limit(limiT)
      )
    ).pipe(
      map((arr) => this.formattedResults(arr)),
      catchError((x) => of(x))
    );
  }

  nextPage(last, limiT: number): Observable<Book[]> {
    const { title, quantity } = last;
    const varNames = Object.keys({ title, quantity });
    console.log(varNames);
    return collectionChanges<Book>(
      query<Book>(
        this.bookColl,
        where('quantity', '>', 0),
        orderBy('quantity'),
        orderBy('title'),
        startAfter(quantity, title),
        limit(limiT)
      )
    ).pipe(
      map((arr) => this.formattedResults(arr)),
      catchError((x) => {
        return of([]);
      })
    );
  }

  prevPage(first: any, limiT: number): Observable<Book[]> {
    const { title, quantity } = first;
    return collectionChanges<Book>(
      query<Book>(
        this.bookColl,
        where('quantity', '>', 0),
        orderBy('quantity'),
        orderBy('title'),
        endBefore(quantity, title),
        limitToLast(limiT)
      )
    ).pipe(
      map((arr) => this.formattedResults(arr)),
      catchError((x) => of(x))
    );
  }

  prevFromLast(limiT: number): Observable<Book[]> {
    return collectionChanges<Book>(
      query<Book>(
        this.bookColl,
        where('quantity', '>', 0),
        orderBy('quantity'),
        orderBy('title'),
        limitToLast(limiT)
      )
    ).pipe(
      map((arr) => this.formattedResults(arr)),
      catchError((x) => of(x))
    );
  }

  formattedResults(arr: DocumentChange<Book>[]): Book[] {
    console.log(arr.map((x) => x.doc.data()));
    return arr?.length > 0
      ? arr?.map((snap) => ({
          ...snap?.doc?.data(),
          id: snap?.doc?.id,
        }))
      : [];
  }

  getTags() {
    const tagDoc = doc(
      this.tagColl,
      'wL7jXIhEZS5dm5qIN4l5'
    ) as DocumentReference<any>;
    return docData(tagDoc, { idField: 'id' }).pipe(
      map((a) => a?.tags),
      catchError((x) => of(x))
    );
  }

  updateTags(tags: string[]) {
    const tagRef = doc(
      this.afs,
      'tags',
      'wL7jXIhEZS5dm5qIN4l5'
    ) as DocumentReference<any>;
    return setDoc(
      tagRef,
      {
        tags: arrayUnion(...tags) as any,
      },
      { merge: true }
    ).catch();
  }
}
